// hydration schema + Bedrock tool definition (PLAN.md §6.1 Batch 5, D-18, D-19).
// Per-event capture: every drink is its own record (D-19) — enables caffeine→sleep,
// alcohol→symptom, and late-fluid correlations. Pure data only — safe for both the
// API (Lambda) and the web SPA.

export const FLUID_TYPES = [
  "water",
  "coffee",
  "tea",
  "juice",
  "soda",
  "sports-drink",
  "protein-shake",
  "alcohol",
  "other",
] as const;
export type FluidType = (typeof FLUID_TYPES)[number];

export const FLUID_UNITS = ["oz", "mL", "cup", "L", "glass"] as const;
export type FluidUnit = (typeof FLUID_UNITS)[number];

export const FLUID_TEMPERATURES = ["hot", "cold", "room"] as const;
export type FluidTemperature = (typeof FLUID_TEMPERATURES)[number];

/** Multiplier from each unit to milliliters (glass ≈ a standard 8 oz glass). */
export const FLUID_UNIT_ML: Record<FluidUnit, number> = {
  oz: 29.5735,
  mL: 1,
  cup: 236.588,
  L: 1000,
  glass: 236.588,
};

/** Fluid types that contain caffeine by default (user/AI estimate can override). */
export const CAFFEINATED_FLUIDS: FluidType[] = ["coffee", "tea"];

/** Payload for a `hydration` event (PLAN.md §6.1 Batch 5). */
export interface HydrationPayload {
  consumedAt: string; // R — ISO-8601 with offset (auto-now per D-10)
  volume: number; // R — numeric as the user said it
  unit: FluidUnit; // R
  volumeMl: number; // D — normalized to mL for trend math
  fluidType: FluidType; // R — default water
  containsCaffeine: boolean; // D — from fluidType / caffeineMgEstimate
  containsAlcohol: boolean; // D — from fluidType / alcoholServings
  caffeineMgEstimate?: number; // O
  alcoholServings?: number; // O
  temperature?: FluidTemperature; // O
  notes?: string; // O
  // ── Paired beverage log (BEVERAGE_BARCODE_PLAN.md D-BEV-2, §4.4) ──
  intakePairId?: string; // O — uuid shared with the sibling food event
  siblingEventType?: "food" | "hydration";
  siblingEventId?: string;
  siblingOccurredAt?: string;
  productName?: string; // tray label, e.g. "Mountain Dew" instead of "Soda"
  barcode?: string;
  userFoodId?: string;
  portion?: number; // O — 0.5 when a half serving was logged (shown as "½")
}

/**
 * Bedrock tool: record a fluid intake. Provide the volume + unit as the user
 * stated it (the server normalizes to mL). Default fluidType to "water" if the
 * user just gives a volume or says "water". For coffee/tea/soda you may suggest a
 * typical caffeineMgEstimate (e.g. 12 oz coffee ≈ 120 mg) and confirm it.
 */
export const RECORD_HYDRATION_TOOL = {
  name: "record_hydration",
  description:
    "Record a drink / fluid intake (water, coffee, tea, juice, soda, sports drink, protein shake, alcohol, etc.). " +
    "Provide volume and unit as the user stated them. Default fluidType to 'water' if they just give " +
    "a volume or say water. Omit consumedAt unless the user stated a time. For caffeinated or " +
    "alcoholic drinks you may include a sensible caffeineMgEstimate or alcoholServings. Ready-to-drink " +
    "protein shakes use fluidType 'protein-shake'.",
  input_schema: {
    type: "object",
    properties: {
      consumedAt: { type: "string", description: "ISO-8601 with UTC offset. Omit if no time was given." },
      volume: { type: "number", description: "Amount as the user said it." },
      unit: { type: "string", enum: [...FLUID_UNITS], description: "oz / mL / cup / L / glass." },
      fluidType: { type: "string", enum: [...FLUID_TYPES], description: "Default water if unspecified." },
      caffeineMgEstimate: { type: "number", description: "Estimated caffeine in mg, if relevant." },
      alcoholServings: { type: "number", description: "Standard servings, for alcoholic drinks." },
      temperature: { type: "string", enum: [...FLUID_TEMPERATURES] },
      notes: { type: "string" },
    },
    required: ["volume", "unit"],
  },
} as const;

// ─── Log Water page (graphical hydration tracker) ────────────────────────────
// The tap-to-log Water page (web `WaterPane`) builds entirely on the hydration
// event above: a tapped container is a `hydration` event whose volume comes from
// the catalog below. These helpers are the single source of truth for the page's
// fluid metadata, container sizes, goal math, and unit display so the API and SPA
// agree. Volumes are kept canonical in fluid ounces (catalog) and milliliters
// (events store volumeMl), so a user switching display units never corrupts totals.

/** Display preference for amounts on the Water page (a subset of FLUID_UNITS the
 *  UI exposes — US customary vs metric). Stored on the patient; default "oz". */
export const FLUID_DISPLAY_UNITS = ["oz", "mL"] as const;
export type FluidDisplayUnit = (typeof FLUID_DISPLAY_UNITS)[number];

/** Default daily intake goal when the patient hasn't set one (64 oz ≈ 1893 mL). */
export const DEFAULT_WATER_GOAL_OZ = 64;
export const DEFAULT_WATER_GOAL_ML = Math.round(DEFAULT_WATER_GOAL_OZ * FLUID_UNIT_ML.oz);

/** Largest single drink the Log Water editor will save (about 270 oz). */
export const MAX_HYDRATION_EDIT_ML = 8000;

/** Per-fluid presentation: label, emoji glyph, and an accent hex for tints/bars. */
export interface FluidMeta {
  label: string;
  emoji: string;
  accent: string; // hex — chip/bar/pour tint for this fluid
}

export const FLUID_META: Record<FluidType, FluidMeta> = {
  water: { label: "Water", emoji: "💧", accent: "#38bdf8" }, // sky-400
  coffee: { label: "Coffee", emoji: "☕", accent: "#a16207" }, // yellow-700
  tea: { label: "Tea", emoji: "🍵", accent: "#65a30d" }, // lime-600
  soda: { label: "Soda", emoji: "🥤", accent: "#dc2626" }, // red-600
  juice: { label: "Juice", emoji: "🧃", accent: "#ea580c" }, // orange-600
  "sports-drink": { label: "Sports", emoji: "🏃", accent: "#0891b2" }, // cyan-600
  "protein-shake": { label: "Protein", emoji: "💪", accent: "#0d9488" }, // teal-600
  alcohol: { label: "Alcohol", emoji: "🍷", accent: "#9333ea" }, // purple-600
  other: { label: "Other", emoji: "🥛", accent: "#64748b" }, // slate-500
};

/** Order the fluid pills render in (water first / default). */
export const FLUID_PILL_ORDER: FluidType[] = [
  "water", "coffee", "tea", "soda", "juice", "sports-drink", "protein-shake", "alcohol", "other",
];

/** A tappable container on the shelf. `oz` is the canonical size; mL is derived
 *  so display in either unit is exact. `kind` selects which SVG vessel to draw. */
export interface FluidContainer {
  id: string;
  label: string;
  oz: number;
  kind: "glass" | "bottle" | "tumbler" | "mug" | "cup" | "can" | "box" | "wine" | "shot" | "pint";
}

/** Context-aware vessel shelf: the containers swap to match the selected fluid so
 *  every tap maps to a real-world size (a 40 oz Stanley of espresso makes no sense). */
export const FLUID_CONTAINERS: Record<FluidType, FluidContainer[]> = {
  water: [
    { id: "glass", label: "Glass", oz: 8, kind: "glass" },
    { id: "bottle", label: "Bottle", oz: 16.9, kind: "bottle" },
    { id: "tumbler", label: "Tumbler", oz: 24, kind: "tumbler" },
    { id: "stanley", label: "Stanley", oz: 40, kind: "tumbler" },
  ],
  coffee: [
    { id: "espresso", label: "Espresso", oz: 2, kind: "shot" },
    { id: "mug", label: "Mug", oz: 12, kind: "mug" },
    { id: "grande", label: "Grande", oz: 16, kind: "cup" },
    { id: "venti", label: "Venti", oz: 20, kind: "cup" },
  ],
  tea: [
    { id: "teacup", label: "Teacup", oz: 8, kind: "mug" },
    { id: "mug", label: "Mug", oz: 12, kind: "mug" },
    { id: "bottled", label: "Bottled", oz: 18, kind: "bottle" },
  ],
  soda: [
    { id: "mini", label: "Mini can", oz: 7.5, kind: "can" },
    { id: "can", label: "Can", oz: 12, kind: "can" },
    { id: "bottle", label: "Bottle", oz: 20, kind: "bottle" },
  ],
  juice: [
    { id: "box", label: "Juice box", oz: 6.75, kind: "box" },
    { id: "glass", label: "Glass", oz: 8, kind: "glass" },
    { id: "bottle", label: "Bottle", oz: 12, kind: "bottle" },
  ],
  "sports-drink": [
    { id: "small", label: "Small", oz: 12, kind: "bottle" },
    { id: "bottle", label: "Bottle", oz: 20, kind: "bottle" },
    { id: "large", label: "Large", oz: 32, kind: "bottle" },
  ],
  "protein-shake": [
    { id: "ensure", label: "Small", oz: 8, kind: "bottle" },
    { id: "premier", label: "Shake", oz: 11, kind: "bottle" },
    { id: "bottle", label: "Bottle", oz: 14, kind: "bottle" },
  ],
  alcohol: [
    { id: "shot", label: "Shot", oz: 1.5, kind: "shot" },
    { id: "wine", label: "Wine", oz: 5, kind: "wine" },
    { id: "beer", label: "Beer", oz: 12, kind: "can" },
    { id: "pint", label: "Pint", oz: 16, kind: "pint" },
  ],
  other: [
    { id: "small", label: "Small", oz: 8, kind: "glass" },
    { id: "medium", label: "Medium", oz: 16, kind: "glass" },
    { id: "large", label: "Large", oz: 24, kind: "glass" },
  ],
};

/** Whether a fluid counts toward the hydration goal. Alcohol is a diuretic, so it
 *  is logged + shown but excluded from the daily total (per design decision). */
export function fluidCountsTowardGoal(fluidType: FluidType): boolean {
  return fluidType !== "alcohol";
}

/** Convert a mL amount into the display-unit numeric value. */
export function mlToDisplay(ml: number, unit: FluidDisplayUnit): number {
  return unit === "oz" ? ml / FLUID_UNIT_ML.oz : ml;
}

/** Round to at most one decimal, dropping a trailing ".0". */
function trim1(n: number): string {
  return (Math.round(n * 10) / 10).toString();
}

/** Format a mL amount in the patient's preferred unit, e.g. "16.9 oz" / "500 mL"
 *  / "1.9 L" (mL auto-promotes to litres at/above 1000). */
export function formatVolume(ml: number, unit: FluidDisplayUnit): string {
  if (unit === "oz") return `${trim1(ml / FLUID_UNIT_ML.oz)} oz`;
  return ml >= 1000 ? `${trim1(ml / 1000)} L` : `${Math.round(ml)} mL`;
}

/** One-line summary of a hydration event payload for Logs rows. Names the product
 *  and flags a paired food row (the caffeine lives on the food half by design). */
export function hydrationSummaryLine(p: Record<string, unknown>): string {
  const extras = [
    p.caffeineMgEstimate != null && `${p.caffeineMgEstimate}mg caffeine`,
    p.alcoholServings != null && `${p.alcoholServings} serving${p.alcoholServings === 1 ? "" : "s"} alcohol`,
  ]
    .filter(Boolean)
    .join(", ");
  const product = typeof p.productName === "string" && p.productName ? `${p.productName} · ` : "";
  const paired = typeof p.intakePairId === "string" && p.intakePairId ? " · also logged as food" : "";
  return `${product}${p.volume} ${p.unit} ${p.fluidType} (${p.volumeMl} mL)${extras ? ` · ${extras}` : ""}${paired}`;
}

/** One-line summary of a food event payload for Logs rows. Shows caffeine and flags
 *  a paired drink row. */
export function foodSummaryLine(p: Record<string, unknown>): string {
  const items = Array.isArray(p.items) ? (p.items as string[]).join(", ") : "";
  const meal = p.mealType ? `${p.mealType}: ` : "";
  const tags = Array.isArray(p.flagged) && p.flagged.length ? ` [${(p.flagged as string[]).join(", ")}]` : "";
  const caffeine = typeof p.caffeineMg === "number" && p.caffeineMg > 0 ? ` · ${Math.round(p.caffeineMg)} mg caffeine` : "";
  const paired = typeof p.intakePairId === "string" && p.intakePairId ? " · also logged as a drink" : "";
  return `${meal}${items}${tags}${caffeine}${paired}`;
}
